import {useCallback, useState} from 'react';
import {useFocusEffect} from '@react-navigation/native';
import i18n from '../../i18n';
import {getUserInfo} from './storage';
import {getManagementScope, fetchPermissionCatalog} from './managementApi';
import {isModuleEnabled} from '../config/featureFlags';
import {isDemoMode} from '../demo/demoState';
import {FLEET_VIEW_PERMISSION, canViewFleet} from '../config/fleetAccess';

const safeArray = (value) => (Array.isArray(value) ? value : []);
const normalizeKey = (value) => String(value || '').trim().toLowerCase();

const ADMIN_BYPASS_ROLES = new Set([
    'system:admin',
    'restaurantStaff:admin',
    'restaurantAdmin',
    'restaurantOwner',
]);

export const SALES_MODULES = ['1 POS License', 'Happy POS mobile app', 'Webshop / Own application', 'Webshop / Own application (Fixed-term)', 'Kiosk'];
export const FINANCE_MODULES = ['Billing module', 'Cash register module', 'Extended statistics', 'Finance module'];
export const CRM_MODULES = ['Customer database + VIP', 'CRM module'];
export const SERVICE_MODULES = ['Service management module'];
export const WAREHOUSE_MODULES = ['Inventory/warehouse module', 'Procurement module'];
// Matches the exact `value` field of the VoIP license entries in
// HappyPOS-backend/src/api/constants/licenseTypes.js (both the recurring
// service and the one-time setup fee use the same "voip-phone-services" value).
export const VOIP_MODULES = ['voip-phone-services'];
const WEBSHOP_MODULES = ['Webshop / Own application', 'Webshop / Own application (Fixed-term)', 'Shopify', 'UNAS', 'WooCommerce', 'BaseLinker'];
const LOGISTICS_MODULES = ['Courier module', 'Dispatcher module', 'GLS', 'UPS', 'iLogistic', 'Wolt Drive'];

const STATIC_PERMISSION_CATALOG = [
    {kind: 'visibility', module: 'sales', key: 'menu.sales', impliedByRoles: ['system:admin', 'sales:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'sales', key: 'page.orders', impliedByRoles: ['system:admin', 'sales:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'restaurantStaff:cashier']},
    {kind: 'visibility', module: 'sales', key: 'page.pos', impliedByRoles: ['system:admin', 'sales:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'restaurantStaff:cashier']},
    {kind: 'permission', module: 'sales', key: 'orders.view', impliedByRoles: ['system:admin', 'sales:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'restaurantStaff:cashier']},
    {kind: 'visibility', module: 'crm', key: 'menu.crm', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'sales:salesman', 'restaurantStaff:admin']},
    {kind: 'visibility', module: 'crm', key: 'page.leads', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'sales:salesman']},
    {kind: 'visibility', module: 'crm', key: 'page.customers', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'crm', key: 'page.quotes', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'sales:salesman']},
    {kind: 'visibility', module: 'crm', key: 'page.dashboard', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'sales:salesman']},
    {kind: 'permission', module: 'crm', key: 'leads.edit', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'sales:salesman']},
    {kind: 'permission', module: 'crm', key: 'customers.edit', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'restaurantStaff:admin']},
    {kind: 'permission', module: 'crm', key: 'quotes.create', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'sales:salesman']},
    {kind: 'visibility', module: 'finance', key: 'menu.finance', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'finance', key: 'page.invoices', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'finance', key: 'page.cashflow', impliedByRoles: ['system:admin', 'restaurantStaff:admin']},
    {kind: 'visibility', module: 'finance', key: 'page.reports', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'permission', module: 'finance', key: 'invoices.view', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'permission', module: 'finance', key: 'reports.view', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'warehouse', key: 'menu.warehouse', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'stock:manager']},
    {kind: 'visibility', module: 'warehouse', key: 'page.inventory', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'stock:manager']},
    {kind: 'visibility', module: 'warehouse', key: 'page.products', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'stock:manager']},
    {kind: 'permission', module: 'warehouse', key: 'inventory.edit', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'stock:manager']},
    {kind: 'permission', module: 'warehouse', key: 'products.edit', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'stock:manager']},
    // Procurement (Beszerzés) — mirrors the four `procurement.page.*` rows of the
    // backend permissionCatalog, so a non-admin buyer still resolves the module
    // when the live catalog fetch has not landed (cold start / offline).
    {kind: 'visibility', module: 'procurement', key: 'page.requisitions', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'procurement', key: 'page.rfqs', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'procurement', key: 'page.orders', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'procurement', key: 'page.settings', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    // Driver / courier (Sofor) app: mirrors the backend `driver.*` rows so a
    // courier resolves the module before the live catalog fetch lands.
    {kind: 'visibility', module: 'driver', key: 'app.use', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'restaurantStaff:delivery', 'delivery:driver', 'delivery:manager']},
    {kind: 'permission', module: 'driver', key: 'sales.create', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'restaurantStaff:delivery', 'delivery:driver', 'delivery:manager']},
    {kind: 'permission', module: 'driver', key: 'invoices.send', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'restaurantStaff:delivery', 'delivery:driver', 'delivery:manager']},
    {kind: 'permission', module: 'driver', key: 'crm.create', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'restaurantStaff:delivery', 'delivery:driver', 'delivery:manager']},
    // Utility bills & meters (Rezsi nyilvántartás) — mirrors the backend
    // `utilities.records.*` permissions (`UTILITY_PERMISSIONS`) and the roles it
    // implies (`UTILITY_IMPLIED_ROLES` + the full-access roles). A plain
    // store-level user (cashier, server, ...) is deliberately NOT listed.
    {kind: 'permission', module: 'utilities', key: 'records.view', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'permission', module: 'utilities', key: 'records.manage', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'customers', key: 'page.customers', impliedByRoles: ['system:admin', 'sales:admin', 'sales:manager', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'reports', key: 'menu.statistics', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'reports', key: 'page.dashboard', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'permission', module: 'reports', key: 'statistics.view', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'automation', key: 'menu.automation', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'settings', key: 'menu.settings', impliedByRoles: ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager']},
    {kind: 'visibility', module: 'settings', key: 'page.users', impliedByRoles: ['system:admin', 'restaurantStaff:admin']},
    {kind: 'visibility', module: 'settings', key: 'page.integrations', impliedByRoles: ['system:admin', 'restaurantStaff:admin']},
    {kind: 'permission', module: 'settings', key: 'users.manage', impliedByRoles: ['system:admin', 'restaurantStaff:admin']},
    {kind: 'permission', module: 'settings', key: 'restaurant.edit', impliedByRoles: ['system:admin', 'restaurantStaff:admin']},
    // Developer/system tooling (deployments, later backup/CI-CD/cron) is implied
    // only by the platform-admin role; restaurant admins still see it through the
    // generic hasFullAccess bypass, matching every other management surface.
    {kind: 'visibility', module: 'system', key: 'menu.developer', impliedByRoles: ['system:admin']},
    {kind: 'visibility', module: 'service', key: 'menu.service', impliedByRoles: ['system:admin', 'serviceAdmin', 'serviceAdvisor', 'serviceTechnician', 'serviceExternal', 'serviceViewer']},
    ...['dashboard', 'notifications', 'tickets', 'vehicles', 'assets', 'bookings', 'calculator', 'customers', 'workflows', 'documents', 'quotes', 'parts'].map((key) => ({
        kind: 'visibility',
        module: 'service',
        key: `page.${key}`,
        impliedByRoles: key === 'tickets'
            ? ['system:admin', 'serviceAdmin', 'serviceAdvisor', 'serviceTechnician', 'serviceExternal', 'serviceViewer']
            : ['dashboard', 'notifications', 'assets'].includes(key)
                ? ['system:admin', 'serviceAdmin', 'serviceAdvisor', 'serviceTechnician', 'serviceExternal']
                : ['system:admin', 'serviceAdmin', 'serviceAdvisor'],
    })),
];

/*
 * Live permission catalog cache. `resolveManagementPermissions` reads
 * whichever is freshest via `getEffectiveCatalog()` — the backend-driven
 * catalog once it has loaded at least once, falling back to
 * `STATIC_PERMISSION_CATALOG` before the first fetch resolves or if the
 * device is offline. This is what lets newly-added capability keys (and any
 * module the catalog didn't know about yet, e.g. rental/meetings/voip) reach
 * mobile without an app update.
 */
let liveCatalogEntries = null;
let liveCatalogFetchedAt = 0;
const LIVE_CATALOG_TTL_MS = 5 * 60 * 1000;

/**
 * Refresh the live permission catalog from `GET /permission-definitions`.
 * Safe to call repeatedly — throttled by `LIVE_CATALOG_TTL_MS` unless
 * `force` is set (e.g. pull-to-refresh on the Management hub). Failures are
 * swallowed so a network blip never breaks access resolution — the previous
 * cache (or the static fallback) keeps serving.
 * @param {boolean} [force]
 * @returns {Promise<void>}
 */
export const refreshLiveManagementCatalog = async (force = false) => {
    if (!force && liveCatalogEntries && (Date.now() - liveCatalogFetchedAt) < LIVE_CATALOG_TTL_MS) {
        return;
    }
    try {
        const entries = await fetchPermissionCatalog();
        if (Array.isArray(entries) && entries.length) {
            liveCatalogEntries = entries;
            liveCatalogFetchedAt = Date.now();
        }
    } catch (_e) {
        // Offline / request failure — keep serving whatever is already cached.
    }
};

const getEffectiveCatalog = () => (liveCatalogEntries?.length ? liveCatalogEntries : STATIC_PERMISSION_CATALOG);

const readCapabilityValue = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'string') return entry;
    return entry.licenseType || entry.moduleKey || entry.module || entry.key || entry.value || entry.name || entry.type || null;
};

const capabilityMatches = (enabled, capability) => {
    const normalized = normalizeKey(capability);
    return enabled.has(normalized) || Array.from(enabled).some((key) => key.includes(normalized) || normalized.includes(key));
};

export const collectRestaurantCapabilityKeys = (restaurant = {}) => {
    const keys = new Set();
    const append = (value) => {
        const raw = readCapabilityValue(value);
        if (raw) keys.add(normalizeKey(raw));
    };

    [
        restaurant?.activeModules,
        restaurant?.enabledModules,
        restaurant?.modules,
        restaurant?.activeLicenses,
        restaurant?.enabledLicenses,
        restaurant?.licenses,
        restaurant?.licenses?.type,
        restaurant?.plan?.activeModules,
        restaurant?.plan?.modules,
        restaurant?.plan?.licenses,
        restaurant?.subscription?.activeModules,
        restaurant?.subscription?.modules,
        restaurant?.subscription?.licenses,
        restaurant?.billing?.activeModules,
        restaurant?.billing?.modules,
        restaurant?.billing?.licenses,
        restaurant?.customerBilling?.modules,
        restaurant?.customerBilling?.licenses,
    ].forEach((collection) => {
        if (Array.isArray(collection)) {
            collection.forEach(append);
            return;
        }
        append(collection);
    });

    append(restaurant?.plan?.currentPlan);
    append(restaurant?.currentPlan);
    return keys;
};

export const hasRestaurantCapability = (restaurant, capabilities = []) => {
    // Demo (Bemutató) mode presents the full product, so every module/licence gate
    // is treated as enabled regardless of what the restaurant actually licenses.
    if (isDemoMode()) return true;
    if (!capabilities || capabilities.length === 0) return true;
    const enabled = collectRestaurantCapabilityKeys(restaurant);
    return safeArray(capabilities).some((capability) => capabilityMatches(enabled, capability));
};

const addRoleValue = (roles, value) => {
    if (!value) return;
    if (Array.isArray(value)) {
        value.forEach((item) => addRoleValue(roles, item));
        return;
    }
    roles.add(String(value));
};

const collectRoles = (user, restaurant, place) => {
    const roles = new Set();
    addRoleValue(roles, user?.roles);
    addRoleValue(roles, user?.userRoles);
    addRoleValue(roles, user?.role);
    addRoleValue(roles, user?.defaultRoles);
    addRoleValue(roles, user?.effectiveRoles);
    addRoleValue(roles, restaurant?.roles);
    addRoleValue(roles, restaurant?.role);
    addRoleValue(roles, place?.roles);
    addRoleValue(roles, place?.role);

    safeArray(user?.dynamicRoles).forEach((role) => {
        if (!role || role.status === 'inactive') return;
        addRoleValue(roles, role.inheritsRoles);
    });

    safeArray(user?.restaurants).forEach((item) => {
        addRoleValue(roles, item?.roles);
        addRoleValue(roles, item?.role);
    });

    return roles;
};

const addVisibilityValue = (visibility, key, value = true) => {
    if (!key || !value) return;
    visibility.add(String(key));
};

const addVisibility = (visibility, value) => {
    if (!value) return;
    if (Array.isArray(value)) {
        value.forEach((key) => addVisibilityValue(visibility, key));
        return;
    }
    const entries = value instanceof Map ? Array.from(value.entries()) : Object.entries(value || {});
    entries.forEach(([key, enabled]) => addVisibilityValue(visibility, key, enabled));
};

const addPermissions = (permissions, value) => {
    safeArray(value).forEach((permission) => permission && permissions.add(String(permission)));
};

/**
 * Collect visibility keys the admin explicitly switched OFF for this user (value
 * === false) in the per-user menu-visibility editor. These denials override the
 * role-implied defaults so a menu can be hidden for one staff member without
 * touching their role.
 * @param {object|Map} value - `user.visibility` / `user.visibilityFlags` map.
 * @returns {Set<string>} The set of explicitly denied visibility keys.
 */
const collectDeniedVisibilityKeys = (value) => {
    const denied = new Set();
    if (!value) return denied;
    const entries = value instanceof Map ? Array.from(value.entries()) : Object.entries(value || {});
    entries.forEach(([key, enabled]) => {
        if (key && enabled === false) denied.add(String(key));
    });

    return denied;
};

export const resolveManagementPermissions = (user, restaurant, place) => {
    const roles = collectRoles(user, restaurant, place);
    const permissions = new Set();
    const visibility = new Set();
    // Demo (Bemutató) mode grants full access so every management menu/tab/page/guard
    // opens for the presenter, mirroring an admin — regardless of the real role.
    const hasFullAccess = isDemoMode() || Array.from(roles).some((role) => ADMIN_BYPASS_ROLES.has(role));

    addPermissions(permissions, user?.permissions);
    addPermissions(permissions, user?.allowedPermissions);
    addPermissions(permissions, user?.permissionKeys);
    addVisibility(visibility, user?.visibility);
    addVisibility(visibility, user?.visibilityFlags);

    safeArray(user?.dynamicRoles).forEach((role) => {
        if (!role || role.status === 'inactive') return;
        addPermissions(permissions, role.permissions);
        addVisibility(visibility, role.visibility);
    });

    // Fleet (Flotta): `/api/fleet/*` has no store-module gate — role / custom right only.
    if (canViewFleet({roles, userRights: user?.userRights})) {
        permissions.add(FLEET_VIEW_PERMISSION);
    }

    getEffectiveCatalog().forEach((entry) => {
        if (!entry?.module || !entry?.key) return;
        const granted = safeArray(entry.impliedByRoles).some((role) => roles.has(role));
        if (!granted) return;
        const fullKey = `${entry.module}.${entry.key}`;
        if (entry.kind === 'visibility') visibility.add(fullKey);
        else permissions.add(fullKey);
    });

    // Per-user denials (from the admin menu-visibility editor) win over role
    // defaults — an admin can hide a specific menu for one staff member. Admins
    // and demo mode are unaffected: they bypass visibility via `hasFullAccess`.
    const denied = new Set([
        ...collectDeniedVisibilityKeys(user?.visibility),
        ...collectDeniedVisibilityKeys(user?.visibilityFlags),
    ]);
    denied.forEach((key) => visibility.delete(key));

    return {roles, permissions, visibility, hasFullAccess};
};

/**
 * The menu/page visibility keys an admin can toggle per user, deduped by their
 * fully-qualified `module.key`. Powers the per-user menu-visibility editor in
 * the Management user detail screen.
 * @returns {Array<{module: string, key: string, fullKey: string, impliedByRoles: string[]}>}
 */
export const getManagementVisibilityCatalog = () => {
    const seen = new Set();
    const catalog = [];
    getEffectiveCatalog().forEach((entry) => {
        if (entry?.kind !== 'visibility' || !entry?.module || !entry?.key) return;
        const fullKey = `${entry.module}.${entry.key}`;
        if (seen.has(fullKey)) return;
        seen.add(fullKey);
        catalog.push({module: entry.module, key: entry.key, fullKey, impliedByRoles: entry.impliedByRoles || []});
    });

    return catalog;
};

// Backend governable module ids (`HappyPOS-backend/src/api/constants/moduleCatalog.js`
// `MODULE_CATALOG`) each rule maps to, for the `Restaurant.enabledModuleIds`
// gate. Rules with neither `moduleId` nor `anyModuleId` are settings-grade
// (no governable id on the backend either — never hidden by the store's
// module allow-list) or, for `integrations`, kept on the legacy
// `capabilities` license-string gate since it has no backend module id yet.
const MANAGEMENT_ACCESS_RULES = {
    dashboard: {
        anyModuleId: ['ertekesites', 'penzugy', 'crm-rendszer', 'szerviz', 'raktarkezeles'],
        anyVisibility: ['reports.page.dashboard', 'sales.page.orders', 'finance.menu.finance', 'crm.menu.crm', 'service.menu.service', 'warehouse.menu.warehouse'],
    },
    data: {
        anyModuleId: ['ertekesites', 'penzugy', 'crm-rendszer', 'szerviz', 'raktarkezeles'],
        anyVisibility: ['sales.page.orders', 'finance.menu.finance', 'crm.menu.crm', 'service.menu.service', 'warehouse.menu.warehouse'],
    },
    automations: {moduleId: 'automatizalas', visibilityKey: 'automation.menu.automation'},
    settings: {visibilityKey: 'settings.menu.settings'},
    store: {visibilityKey: 'settings.menu.settings', requiredPermission: 'settings.restaurant.edit'},
    users: {visibilityKey: 'settings.page.users', requiredPermission: 'settings.users.manage'},
    roles: {visibilityKey: 'settings.page.users', requiredPermission: 'settings.users.manage'},
    moduleTemplates: {visibilityKey: 'settings.menu.settings', requiredPermission: 'settings.restaurant.edit'},
    billing: {visibilityKey: 'settings.menu.settings', requiredPermission: 'settings.restaurant.edit'},
    deployments: {visibilityKey: 'system.menu.developer'},
    // Host telemetry (CPU/memory/PM2/logs/DB/cache/secrets) — platform-admin only,
    // the same developer gate CI/CD sits behind.
    systemStatus: {visibilityKey: 'system.menu.developer'},
    orders: {moduleId: 'ertekesites', visibilityKey: 'sales.page.orders', requiredPermission: 'sales.orders.view'},
    // Orders WRITTEN by the Sales module (a salesperson selling to a lead or a
    // unified customer) — deliberately NOT the `orders` rule above: that one
    // requires `sales.orders.view`, a permission the catalog only implies for
    // admin/manager/cashier roles, so a pure `sales:*` seller would be locked
    // out of the module built for them. Any CRM/sales menu key opens it.
    salesOrders: {anyModuleId: ['ertekesites', 'crm-rendszer'], anyVisibility: ['sales.page.orders', 'crm.menu.crm', 'crm.page.leads', 'crm.page.customers']},
    products: {anyModuleId: ['ertekesites', 'raktarkezeles'], anyVisibility: ['sales.page.orders', 'sales.page.pos', 'warehouse.page.products']},
    finance: {moduleId: 'penzugy', visibilityKey: 'finance.menu.finance'},
    // NovoPayment (accounts, cards, payments, payouts, day close): finance-grade; the backend enforces its own permissions.
    novoPayment: {moduleId: 'penzugy', anyVisibility: ['finance.page.cashflow', 'finance.page.reports']},
    invoicesInbound: {moduleId: 'penzugy', visibilityKey: 'finance.page.invoices', requiredPermission: 'finance.invoices.view'},
    invoicesOutbound: {moduleId: 'penzugy', visibilityKey: 'finance.page.invoices', requiredPermission: 'finance.invoices.view'},
    banks: {moduleId: 'penzugy', anyVisibility: ['finance.page.cashflow', 'finance.page.reports']},
    registers: {moduleId: 'penzugy', anyVisibility: ['finance.page.cashflow', 'finance.page.reports']},
    costTypes: {moduleId: 'penzugy', anyVisibility: ['finance.page.cashflow', 'finance.page.reports']},
    expenses: {moduleId: 'penzugy', anyVisibility: ['finance.page.cashflow', 'finance.page.reports']},
    crm: {moduleId: 'crm-rendszer', visibilityKey: 'crm.menu.crm'},
    leads: {moduleId: 'crm-rendszer', visibilityKey: 'crm.page.leads'},
    // Edit-side gate of the lead category / AI research tools (change category, manage categories, apply research).
    leadsEdit: {moduleId: 'crm-rendszer', visibilityKey: 'crm.page.leads', requiredPermission: 'crm.leads.edit'},
    // Apollo.io prospecting. Gated EXACTLY like the CRM lead list on purpose —
    // no stricter admin/settings permission — because a salesperson IS the
    // intended user: the whole point of the module is that a rep finds a company,
    // reveals a decision-maker and imports the result as a lead they then work.
    // Requiring, say, `settings.restaurant.edit` (the gate the paid integrations
    // use) would leave the module reachable only by the one person who never
    // prospects. `ertekesites` joins `crm-rendszer` in the module list for the
    // same reason `unifiedCustomers` accepts it — a rep can work in a store
    // licensed for sales without the standalone CRM module — and `crm.menu.crm`
    // is accepted beside `crm.page.leads` because the static catalog does not
    // imply the page key for every seller role.
    //
    // The "may this store use Apollo at all" question is answered elsewhere and
    // deliberately NOT duplicated here: `module.apollo` in config/featureFlags.js
    // decides whether the BUILD ships it, and the live integration status +
    // credit balance come from the backend through `hooks/useApolloStatus`.
    apollo: {anyModuleId: ['crm-rendszer', 'ertekesites'], anyVisibility: ['crm.page.leads', 'crm.menu.crm']},
    customers: {anyModuleId: ['crm-rendszer', 'szerviz'], anyVisibility: ['crm.page.customers', 'customers.page.customers', 'service.page.customers']},
    // The UNIFIED customer index (store + partner + order guest + CRM in one
    // list) the Sales module browses. Same gate as `customers` plus the sales
    // module id, because a salesperson reaches it from the sales workspace and
    // may work in a store that licenses `ertekesites` but not `szerviz`.
    // `crm.menu.crm` is accepted on top of `customers`' keys on purpose: the
    // catalog does NOT imply `crm.page.customers` for `sales:salesman`, and a
    // seller who may open the CRM must be able to see who they sell to.
    unifiedCustomers: {anyModuleId: ['crm-rendszer', 'ertekesites', 'szerviz'], anyVisibility: ['crm.page.customers', 'customers.page.customers', 'service.page.customers', 'crm.menu.crm']},
    quotes: {anyModuleId: ['crm-rendszer', 'szerviz'], anyVisibility: ['crm.page.quotes', 'service.page.quotes']},
    // Projects + customer support (admin menu groups `projektek` / `ugyfelszolgalat`).
    // Each list/detail pair self-gates on the same `*.page.*` visibility key the
    // admin sidebar child uses, under its own governable store module.
    projects: {moduleId: 'projektek', visibilityKey: 'projects.page.projects'},
    tasks: {moduleId: 'projektek', visibilityKey: 'projects.page.tasks'},
    complaints: {moduleId: 'ugyfelszolgalat', visibilityKey: 'support.page.complaints'},
    supportTickets: {moduleId: 'ugyfelszolgalat', visibilityKey: 'support.page.tickets'},
    service: {moduleId: 'szerviz', visibilityKey: 'service.menu.service'},
    workOrders: {moduleId: 'szerviz', visibilityKey: 'service.page.tickets'},
    appointments: {moduleId: 'szerviz', visibilityKey: 'service.page.bookings'},
    technicians: {moduleId: 'szerviz', anyVisibility: ['service.page.workflows', 'service.page.dashboard']},
    // Time tracking is a PERSONAL tool, not a management view: everyone logs their
    // own hours, so it deliberately carries no visibility/permission requirement —
    // gating it behind, say, `service.page.dashboard` would lock out the very
    // technician the clock exists for. No module gate either (it used to require
    // `projektek`/`szerviz`, which hid the Modules-hub tile for every store
    // licensing neither): tracked time is workspace-grade, like `settings`, so
    // the store's module allow-list never hides it. The build-time
    // `module.timeTracking` flag in config/featureFlags.js remains its only gate.
    timeTracking: {},
    // Own payslips (Bérjegyzékeim): self-service, the API only returns the caller's own CLOSED payslips.
    myPayslips: {},
    // AI meeting recorder/transcripts — a workspace feature like `timeTracking`
    // above: personal productivity, not a governable store module, so no module
    // gate and no visibility/permission requirement. The build-time
    // `module.meetings` flag in config/featureFlags.js is its only gate.
    meetings: {},
    // Happy AI insights hub — same shape as `meetings`: an AI decision-queue
    // dashboard is a cross-cutting workspace feature, not tied to any one
    // store module, so there is no module/visibility/permission gate here.
    // Gated instead by the build-time `module.happyAi` flag AND, at the
    // screen/entry-point level, by the store's AI kill switch
    // (`useAiAssistantGate` / `isAiAssistantEnabled`).
    happyAi: {},
    // Happy AI business digest / alerts / goals — management-level views (revenue
    // and KPI figures), so unlike `happyAi` they follow the dashboard's reporting
    // visibility key: management/global roles (and admin bypass roles) pass,
    // floor staff do not. Also behind the AI kill switch at screen/entry level.
    businessDigest: {visibilityKey: 'reports.page.dashboard'},
    businessAlerts: {visibilityKey: 'reports.page.dashboard'},
    businessGoals: {visibilityKey: 'reports.page.dashboard'},
    // Finance Intelligence (cashier anomalies, VAT/NAV check, expenses, supplier prices) — management/global
    // only, same gate as `businessDigest`. The cashier tab is further limited to admin/owner/manager by the API (403).
    financeIntelligence: {visibilityKey: 'reports.page.dashboard'},
    // Cost / stock intelligence — visible to management/global roles (reporting
    // key) AND to warehouse staff: `warehouse.page.inventory` is implied by the
    // `stock:manager` role (same key the `warehouses` rule uses), so the
    // stock-centric daily user passes without the dashboard reporting key.
    // Both need one of the commercial modules; dashboard cards additionally
    // require `businessDigest` for the margin card (management only).
    costIntelligence: {
        anyModuleId: ['raktarkezeles', 'ertekesites', 'penzugy'],
        anyVisibility: ['reports.page.dashboard', 'warehouse.page.inventory'],
    },
    stockIntelligence: {
        anyModuleId: ['raktarkezeles', 'ertekesites', 'penzugy'],
        anyVisibility: ['reports.page.dashboard', 'warehouse.page.inventory'],
    },
    stock: {anyModuleId: ['raktarkezeles', 'szerviz'], anyVisibility: ['warehouse.page.inventory', 'service.page.parts']},
    // Gated the same as `service` (module license + menu visibility) — but
    // note the applied service MODE's own feature toggles never affect this
    // key, so a mode can never lock out the screen that undoes it.
    serviceModes: {moduleId: 'szerviz', visibilityKey: 'service.menu.service'},
    logistics: {moduleId: 'raktarkezeles', visibilityKey: 'warehouse.menu.warehouse'},
    warehouses: {moduleId: 'raktarkezeles', visibilityKey: 'warehouse.page.inventory'},
    // Procurement (Beszerzés). Its own governable module id — `beszerzes-csoport`,
    // the admin menu group / MODULE_CATALOG id — NOT `raktarkezeles`: the two are
    // separate licences (the warehouse module does not entitle a store to
    // procurement), and admin's own MENU_CAPABILITY_RULES gates the group on the
    // 'Procurement module' licence alone. Each page then self-gates on its
    // `procurement.page.*` visibility key, matching the admin sidebar children.
    procurement: {
        moduleId: 'beszerzes-csoport',
        anyVisibility: [
            'procurement.page.orders',
            'procurement.page.requisitions',
            'procurement.page.rfqs',
            'procurement.page.settings',
        ],
    },
    // Utility bills & meters (Rezsi nyilvántartás): store module id
    // `rezsi-nyilvantartas` + the backend `utilities.records.*` permissions. The
    // view rule opens the hub tile / read screens, the manage rule the entry forms.
    utilities: {moduleId: 'rezsi-nyilvantartas', anyPermission: ['utilities.records.view', 'utilities.records.manage']},
    utilitiesManage: {moduleId: 'rezsi-nyilvantartas', requiredPermission: 'utilities.records.manage'},
    procurementRequisitions: {moduleId: 'beszerzes-csoport', visibilityKey: 'procurement.page.requisitions'},
    procurementRfqs: {moduleId: 'beszerzes-csoport', visibilityKey: 'procurement.page.rfqs'},
    procurementOrders: {moduleId: 'beszerzes-csoport', visibilityKey: 'procurement.page.orders'},
    // The sourcing planner has no visibility key of its own in the backend
    // permission catalogue (admin's sidebar child points at a key that is never
    // emitted), so it accepts the buyer keys instead of resolving to "no rule"
    // — which would lock the page for every non-admin. Whether the BUILD ships
    // it at all is a separate question, answered by `module.procurementSourcing`
    // in config/featureFlags.js (the App Store review profile switches it off,
    // because an analysis with its narrative spends the store's AI credits).
    procurementSourcing: {
        moduleId: 'beszerzes-csoport',
        anyVisibility: ['procurement.page.orders', 'procurement.page.requisitions', 'procurement.page.settings'],
    },
    procurementSettings: {moduleId: 'beszerzes-csoport', visibilityKey: 'procurement.page.settings'},
    // Driver / courier (Sofor) app. Store-module id `sofor-app`; default-open
    // when the store never customised `enabledModuleIds`.
    // `flotta` (and its former parent `nyilvantartasok`) grant it automatically —
    // mirror of the backend `MODULE_ACCESS_ALIASES['sofor-app']`.
    // Fleet (Flotta) — see config/fleetAccess.js; the backend has no module gate on /api/fleet/*.
    fleet: {requiredPermission: FLEET_VIEW_PERMISSION},
    driver: {anyModuleId: ['sofor-app', 'flotta', 'nyilvantartasok'], visibilityKey: 'driver.app.use'},
    transactions: {moduleId: 'raktarkezeles', visibilityKey: 'warehouse.page.inventory'},
    // No backend governable module id exists for "integrations" — keeps the
    // legacy license-string gate as its only module-level check.
    integrations: {capabilities: [...LOGISTICS_MODULES, ...WEBSHOP_MODULES, ...FINANCE_MODULES], visibilityKey: 'settings.page.integrations'},
    // Admin-mirroring module groups (config/adminModules/*): the module id + menu
    // visibility key are exactly what the admin sidebar group is gated on
    // (admin `menuStructure.js`), so a role sees the same modules on mobile.
    marketing: {moduleId: 'marketing', anyVisibility: ['marketing.menu.marketing', 'loyalty.menu.loyalty']},
    loyalty: {moduleId: 'marketing', visibilityKey: 'loyalty.menu.loyalty'},
    education: {moduleId: 'oktatas', anyVisibility: ['hr.menu.hr', 'recruitment.menu.recruitment']},
    teamChat: {moduleId: 'csapatchat', visibilityKey: 'chat.page.access'},
    mail: {moduleId: 'levelezes', visibilityKey: 'mail.menu.mail'},
    calendar: {moduleId: 'naptaram'},
    hr: {anyModuleId: ['hr-rendszer'], anyVisibility: ['hr.menu.hr', 'recruitment.menu.recruitment']},
    hrEmployees: {moduleId: 'hr-rendszer', visibilityKey: 'hr.page.employees'},
    hrPayroll: {moduleId: 'hr-rendszer', requiredPermission: 'payroll.view'},
    recruitment: {moduleId: 'hr-rendszer', visibilityKey: 'recruitment.menu.recruitment'},
    documents: {moduleId: 'dokumentumkezeles', visibilityKey: 'documents.menu.documents'},
    manufacturing: {moduleId: 'gyartas', visibilityKey: 'manufacturing.menu.manufacturing'},
    rental: {moduleId: 'kolcsonzes', visibilityKey: 'rental.menu.rental'},
    haccp: {moduleId: 'nyilvantartasok', visibilityKey: 'haccp.menu.haccp'},
};

export const getManagementAccessRule = (key) => MANAGEMENT_ACCESS_RULES[key] || null;

/**
 * Pick the Manager dashboard profile for the current store + user. The profile
 * only re-orders the one shared dashboard (area-card order) and adds a
 * secondary, domain-specific KPI row BELOW the management overview — it never
 * hides data the user could otherwise access, never renames the screen and
 * never replaces the management KPI quartet (revenue / orders / average basket
 * / open orders), which always leads. A service- or warehouse-vertical store is
 * still the Manager dashboard when the app runs in manager mode.
 *
 * Resolution order (first match wins):
 *  1. `restaurant.placeType` — a service-/warehouse-vertical store outranks any
 *     role, because the whole business runs that vertical.
 *  2. Dominant user role — service staff, stock managers, and pure sales roles
 *     (no restaurant management role) get their own emphasis.
 *  3. Finance-focused users — may see finance but not day-to-day sales orders.
 *  4. Default `hospitality` (the classic restaurant dashboard).
 *
 * @param {object} params
 * @param {string|string[]} [params.placeType] `restaurant.placeType` (array or string).
 * @param {Set<string>|string[]} [params.roles] Collected user roles (see `resolveManagementPermissions`).
 * @param {Function} [params.canAccess] `(key) => ({allowed})` access checker for visibility-based fallbacks.
 * @returns {'hospitality'|'finance'|'service'|'warehouse'|'crm'} The dashboard profile key.
 */
export const resolveDashboardProfile = ({placeType, roles, canAccess} = {}) => {
    const types = (Array.isArray(placeType) ? placeType : (placeType ? [placeType] : [])).map(normalizeKey);
    const typeMatches = (fragments) => types.some((typeName) => fragments.some((fragment) => typeName.includes(fragment)));
    if (typeMatches(['service', 'szerviz', 'repair', 'workshop', 'garage'])) return 'service';
    if (typeMatches(['warehouse', 'raktar', 'logistic', 'wholesale'])) return 'warehouse';

    const roleSet = roles instanceof Set ? roles : new Set(safeArray(roles).map(String));
    const hasRole = (names) => names.some((name) => roleSet.has(name));
    if (hasRole(['serviceAdmin', 'serviceAdvisor', 'serviceTechnician'])) return 'service';
    if (hasRole(['stock:manager'])) return 'warehouse';
    if (hasRole(['sales:admin', 'sales:manager', 'sales:salesman']) && !hasRole(['restaurantStaff:admin', 'restaurantStaff:manager'])) return 'crm';

    if (typeof canAccess === 'function' && canAccess('finance').allowed && !canAccess('orders').allowed) return 'finance';

    return 'hospitality';
};

export const canAccessManagementKey = (key, context = {}) => {
    // The BUILD-TIME switchboard wins over everything below, demo mode included:
    // if this release was built without a module (config/featureFlags.js), the
    // tile, the tab and the route must all be unreachable — that is exactly what
    // an App Store review build relies on.
    if (!isModuleEnabled(key)) {
        return {allowed: false, reason: i18n.t('management_access_module_not_enabled')};
    }

    // Demo (Bemutató) mode reveals the ENTIRE product up-front — including modules
    // and features added later (rental, etc.) whose access key may not be
    // registered in MANAGEMENT_ACCESS_RULES yet — so a presenter never hits a
    // locked or "no rule" screen. List-free: any key, known or not, is allowed.
    // Session-only, so it reverts on cold start.
    if (isDemoMode()) {
        return {allowed: true, reason: ''};
    }

    const rule = getManagementAccessRule(key);
    if (!rule) {
        return {allowed: false, reason: i18n.t('management_access_no_rule')};
    }

    const restaurant = context.restaurant || context.scope?.restaurant || null;
    const permissionsContext = context.permissionsContext || resolveManagementPermissions(context.user, restaurant, context.place || context.scope?.place);

    // Demo (Bemutató) mode unlocks every module regardless of the store's real
    // license set, so a presenter can walk through all menus and modules. Outside
    // demo mode the license/module gate still applies — even to admins — so real
    // stores only surface the modules they actually have enabled.
    const bypassModuleLicensing = isDemoMode();

    if (!bypassModuleLicensing && (rule.moduleId || rule.anyModuleId?.length)) {
        // `Restaurant.enabledModuleIds` — the real store-level module allow-list
        // (governed by admin's Module Templates feature). `null`/`undefined`
        // means the store never customized it, so every module stays visible
        // (matches admin's own default-open semantics in `StoreModulesPanel.js`).
        const enabledModuleIds = Array.isArray(restaurant?.enabledModuleIds) ? restaurant.enabledModuleIds : null;

        if (enabledModuleIds) {
            const moduleAllowed = rule.moduleId
                ? enabledModuleIds.includes(rule.moduleId)
                : rule.anyModuleId.some((id) => enabledModuleIds.includes(id));

            if (!moduleAllowed) {
                return {allowed: false, reason: i18n.t('management_access_module_not_enabled')};
            }
        }
    } else if (!bypassModuleLicensing && rule.capabilities?.length && !hasRestaurantCapability(restaurant, rule.capabilities)) {
        return {allowed: false, reason: i18n.t('management_access_module_not_enabled')};
    }

    if (permissionsContext.hasFullAccess) {
        return {allowed: true, reason: ''};
    }

    if (rule.visibilityKey && !permissionsContext.visibility.has(rule.visibilityKey)) {
        return {allowed: false, reason: i18n.t('management_access_role_cannot_view_menu')};
    }

    if (rule.anyVisibility?.length && !rule.anyVisibility.some((keyName) => permissionsContext.visibility.has(keyName))) {
        return {allowed: false, reason: i18n.t('management_access_role_cannot_view_menu')};
    }

    if (rule.requiredPermission && !permissionsContext.permissions.has(rule.requiredPermission)) {
        return {allowed: false, reason: i18n.t('management_access_missing_permission')};
    }

    if (rule.anyPermission?.length && !rule.anyPermission.some((keyName) => permissionsContext.permissions.has(keyName))) {
        return {allowed: false, reason: i18n.t('management_access_missing_permission')};
    }

    return {allowed: true, reason: ''};
};

export const buildManagementAccessContext = async () => {
    const [scope, user] = await Promise.all([getManagementScope(), getUserInfo()]);

    await refreshLiveManagementCatalog();

    return {
        scope,
        user,
        restaurant: scope?.restaurant || null,
        place: scope?.place || null,
        permissionsContext: resolveManagementPermissions(user, scope?.restaurant, scope?.place),
    };
};

export const useManagementAccess = () => {
    const [state, setState] = useState({loading: true, context: null});

    const load = useCallback(async () => {
        const context = await buildManagementAccessContext();
        setState({loading: false, context});
    }, []);

    useFocusEffect(useCallback(() => {
        let mounted = true;
        buildManagementAccessContext()
            .then((context) => {
                if (mounted) setState({loading: false, context});
            })
            .catch(() => {
                if (mounted) setState({loading: false, context: null});
            });
        return () => {
            mounted = false;
        };
    }, []));

    const canAccess = useCallback((key) => canAccessManagementKey(key, state.context || {}), [state.context]);

    return {
        ...state,
        reload: load,
        canAccess,
    };
};

/**
 * Maps notification-category module ids to the management-menu visibility keys
 * that a role-gated user must have at least one of to see that notification category.
 * @private
 */
const NOTIFICATION_CATEGORY_VISIBILITY_KEYS = {
    sales: ['sales.menu.sales', 'sales.page.orders', 'sales.page.pos'],
    service: ['service.menu.service', 'service.page.tickets', 'service.page.dashboard'],
    crm: ['crm.menu.crm', 'crm.page.leads', 'crm.page.customers'],
    finance: ['finance.menu.finance', 'finance.page.invoices'],
    warehouse: ['warehouse.menu.warehouse', 'warehouse.page.inventory', 'warehouse.page.products'],
    voip: ['voip.page.phone'],
};

const NOTIFICATION_MODULE_CAPABILITIES_MAP = {
    sales: SALES_MODULES,
    service: SERVICE_MODULES,
    crm: CRM_MODULES,
    finance: FINANCE_MODULES,
    warehouse: WAREHOUSE_MODULES,
    voip: VOIP_MODULES,
};

/**
 * Derive the set of notification-category module ids that should be visible for
 * a given user+restaurant combination. Combines two independent gates:
 *   1. Restaurant licence — the restaurant must have the module's license active.
 *   2. Role access — the user's role must imply at least one of the module's
 *      management-menu visibility keys (from the live permission catalog).
 *
 * Admin roles and demo mode bypass both gates and return `null` (show all).
 * Returns `null` (show all) when no restaurant capability data is available, so
 * the settings screen never renders completely empty for a user with no license data.
 *
 * @param {object|null} user
 * @param {object|null} restaurant
 * @returns {Set<string>|null} Set of visible module ids, or null to show everything.
 */
export const deriveNotificationModuleAccess = (user, restaurant) => {
    const {hasFullAccess, visibility} = resolveManagementPermissions(user || {}, restaurant || {}, null);
    if (hasFullAccess) return null;

    const capabilityKeys = collectRestaurantCapabilityKeys(restaurant || {});
    // No module data: fail-open so the settings screen never shows empty.
    if (!capabilityKeys || capabilityKeys.size === 0) return null;

    return new Set(
        Object.entries(NOTIFICATION_MODULE_CAPABILITIES_MAP)
            .filter(([moduleId, capabilities]) => {
                if (!hasRestaurantCapability(restaurant, capabilities)) return false;
                const visKeys = NOTIFICATION_CATEGORY_VISIBILITY_KEYS[moduleId];
                return !visKeys || visKeys.some((key) => visibility.has(key));
            })
            .map(([moduleId]) => moduleId),
    );
};



